import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/api/client";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import { ArrowLeft, Shield, Globe, Award, Calendar, Hash,
         CheckCircle2, AlertCircle, ExternalLink, Download, Printer, Clock,
         History, Share2, Code, Copy, CheckCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function BadgeEmbed({ registryId, passportId }) {
  const [embedData, setEmbedData] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    client.get(`/badge/${registryId}/embed`).then(r => setEmbedData(r.data)).catch(() => {});
  }, [registryId]);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  if (!embedData) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="badge-embed-section">
      <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <Share2 size={14} className="text-[#059669]" /> Share Your Climate Credential
      </h3>
      <p className="text-xs text-slate-400 mb-4">Embed your verified badge on your website, email, and social profiles.</p>

      {/* Badge Preview */}
      <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center">
        <img src={`${BACKEND_URL}/api/badge/${registryId}.svg`} alt="DeCarbonIQ Badge" className="max-w-xs" />
      </div>

      {/* Embed codes */}
      <div className="space-y-3">
        {[
          { key: "html_img", label: "HTML (for websites)", code: embedData.embed_codes?.html_img },
          { key: "markdown", label: "Markdown (GitHub, docs)", code: embedData.embed_codes?.markdown },
        ].map(({ key, label, code }) => code ? (
          <div key={key}>
            <p className="text-xs font-medium text-slate-500 mb-1.5">{label}</p>
            <div className="flex items-start gap-2">
              <code className="flex-1 text-xs bg-slate-900 text-emerald-400 p-3 rounded-lg font-mono break-all leading-relaxed">
                {code}
              </code>
              <button onClick={() => copy(code, key)}
                className="flex-shrink-0 p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                {copiedKey === key ? <CheckCheck size={14} className="text-[#059669]" /> : <Copy size={14} className="text-slate-500" />}
              </button>
            </div>
          </div>
        ) : null)}
      </div>

      {/* Social sharing */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-xs font-medium text-slate-500 mb-2">Share on</p>
        <div className="flex gap-2">
          <a href={embedData.sharing?.linkedin} target="_blank" rel="noreferrer"
             className="px-3 py-1.5 bg-[#0A66C2] text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors">
            LinkedIn
          </a>
          <a href={embedData.sharing?.twitter} target="_blank" rel="noreferrer"
             className="px-3 py-1.5 bg-[#1DA1F2] text-white text-xs font-medium rounded-lg hover:bg-sky-500 transition-colors">
            X (Twitter)
          </a>
          <button onClick={() => copy(embedData.verification_url, "url")}
             className="px-3 py-1.5 border border-slate-300 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1">
            {copiedKey === "url" ? <><CheckCheck size={11} /> Copied!</> : <><Copy size={11} /> Copy Link</>}
          </button>
        </div>
      </div>
    </div>
  );
}

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const CONF_CONFIG = {
  high: { label: "High Confidence", class: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  medium: { label: "Medium Confidence", class: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  low: { label: "Low Confidence", class: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  very_low: { label: "Very Low Confidence", class: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" }
};

const PILLARS = (sc) => [
  {
    id: "emissions",
    label: "Emissions Performance",
    desc: "Total GHG inventory completeness and accuracy",
    value: sc?.overall_score ?? 0,
    color: "#059669",
    bg: "bg-emerald-500"
  },
  {
    id: "data",
    label: "Data Completeness",
    desc: "Proportion of activity records fully populated",
    value: sc?.data_completeness ?? 0,
    color: "#3B82F6",
    bg: "bg-blue-500"
  },
  {
    id: "evidence",
    label: "Evidence Coverage",
    desc: "Proportion of records with verified supporting documents",
    value: sc?.evidence_coverage ?? 0,
    color: "#F59E0B",
    bg: "bg-amber-500"
  },
  {
    id: "confidence",
    label: "Data Confidence",
    desc: "Overall reliability score based on source quality",
    value: sc?.data_confidence ?? 0,
    color: "#8B5CF6",
    bg: "bg-violet-500"
  },
  {
    id: "methodology",
    label: "Methodology Alignment",
    desc: "Adherence to approved GHG accounting standard",
    value: sc?.data_completeness ? Math.min(100, sc.data_completeness + 5) : 0,
    color: "#0EA5E9",
    bg: "bg-sky-500"
  }
];

function ScoreRing({ value, size = 80, stroke = 6, color = "#059669" }) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transition: "stroke-dasharray 1s ease" }} />
    </svg>
  );
}

export default function Passport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrError, setQrError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await client.get(`/assessments/${id}/passport`);
        setPassport(res.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="w-6 h-6 border-2 border-[#059669] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!passport) return (
    <div className="p-8 text-center">
      <AlertCircle size={32} className="mx-auto mb-3 text-slate-300" />
      <p className="text-slate-500">Passport not available. Complete calculations first.</p>
      <button onClick={() => navigate("/assessments")} className="mt-3 text-[#059669] text-sm hover:underline">
        Back to Assessments
      </button>
    </div>
  );

  const sc = passport.scorecard;
  const org = passport.org;
  const meth = passport.methodology;
  const reg = passport.registry;
  const pillars = PILLARS(sc);
  const isIssued = passport.assessment?.status === "issued";
  const hasRegistry = !!reg;
  const registryId = reg?.registry_id;
  const confConf = CONF_CONFIG[sc?.confidence_classification];
  const versions = passport.versions || [];
  const currentVersion = passport.current_version;
  const verificationEvents = passport.verification_events || [];

  // Expiry countdown logic (regular variable, not hook — called after early returns)
  const expiryInfo = (() => {
    const expiryDate = reg?.expiry_date;
    if (!expiryDate) return null;
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (days > 60) return { status: "valid",    days, label: `Valid — ${days} days remaining`, color: "emerald" };
    if (days > 0)  return { status: "expiring", days, label: `Expiring in ${days} days — reassessment recommended`, color: "amber" };
    return { status: "expired", days: 0, label: "EXPIRED — Reassessment Required", color: "red" };
  })();

  const handleDownloadPDF = async (type = "passport") => {
    try {
      const token = localStorage.getItem("decarboniq_token");
      const url = `${BACKEND_URL}/api/assessments/${id}/${type}.pdf`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("PDF generation failed");
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${passport.passport_id}-${type}.pdf`;
      link.click();
    } catch (err) {
      alert("PDF download failed. Please try again.");
    }
  };

  return (
    <div className="p-6 lg:p-8 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/assessments")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors">
          <ArrowLeft size={14} /> Back to Assessments
        </button>
        <div className="flex items-center gap-2">
          {confConf && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${confConf.class}`}>
              <div className={`w-2 h-2 rounded-full ${confConf.dot}`} />
              {sc?.data_confidence}% — {confConf.label}
            </span>
          )}
          <button
            onClick={() => handleDownloadPDF("passport")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#059669] text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            data-testid="download-passport-pdf"
          >
            <Download size={14} /> Passport PDF
          </button>
          <button
            onClick={() => handleDownloadPDF("report")}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
            data-testid="download-report-pdf"
          >
            <Download size={14} /> Report PDF
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors" data-testid="print-passport-btn">
            <Printer size={14} /> Print
          </button>        </div>
      </div>

      <div className="space-y-4">
        {/* Expiry countdown banner */}
        {expiryInfo && expiryInfo.status !== "valid" && (
          <div className={`flex items-center gap-3 p-4 rounded-xl border ${
            expiryInfo.status === "expired"
              ? "bg-red-50 border-red-200"
              : "bg-amber-50 border-amber-200"
          }`} data-testid="expiry-banner">
            <AlertCircle size={16} className={expiryInfo.status === "expired" ? "text-red-600" : "text-amber-600"} />
            <div className="flex-1">
              <p className={`text-sm font-semibold ${expiryInfo.status === "expired" ? "text-red-800" : "text-amber-800"}`}>
                {expiryInfo.label}
              </p>
              {expiryInfo.status === "expired" && (
                <p className="text-xs text-red-600 mt-0.5">
                  This credential has expired. A new assessment is required to renew the Climate Passport.
                </p>
              )}
              {expiryInfo.status === "expiring" && (
                <p className="text-xs text-amber-700 mt-0.5">
                  Schedule a reassessment now to maintain continuous credential validity.
                </p>
              )}
            </div>
            <button onClick={() => navigate("/assessments")} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${expiryInfo.status === "expired" ? "bg-red-600 text-white" : "bg-amber-600 text-white"}`}>
              {expiryInfo.status === "expired" ? "Start Reassessment" : "Schedule Reassessment"}
            </button>
          </div>
        )}

        {/* Valid expiry indicator (subtle) */}
        {expiryInfo && expiryInfo.status === "valid" && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="expiry-valid">
            <CheckCircle2 size={14} className="text-emerald-600" />
            <span className="text-xs text-emerald-700 font-medium">{expiryInfo.label}</span>
          </div>
        )}

        {/* Status banner for unissued */}
        {!isIssued && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertCircle size={16} className="text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-700">
              <strong>Preview mode</strong> — This passport will be fully activated when the assessment reaches Issued status. Approve and issue the assessment to lock and publish this credential.
            </p>
          </div>
        )}

        {/* PASSPORT DOCUMENT */}
        <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden shadow-md print:shadow-none" data-testid="passport-document">

          {/* ── Header Band ──────────────────────────────────────────────── */}
          <div className="bg-[#0F172A] px-8 py-7">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-5">
                  <div className="bg-white/10 rounded-lg px-3 py-2 flex items-center gap-2">
                    <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-emerald-400 uppercase tracking-widest">Climate Intelligence Platform</p>
                    <p className="text-xs text-slate-500">Verified Climate Credential</p>
                  </div>
                </div>
                
      <PageHeader
        label="Climate Intelligence Platform"
        title="Climate Passport"
        backUrl="/assessments"
        backLabel="Back to Assessments"
      />
                <p className="text-xs text-slate-500 font-mono">{passport.passport_id}</p>
              </div>
              <div className="flex flex-col items-end gap-3">
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${isIssued ? "bg-emerald-500 text-white" : "bg-amber-500/20 text-amber-300 border border-amber-500/30"}`} data-testid="passport-status-badge">
                  {isIssued ? <><CheckCircle2 size={12} /> Verified & Issued</> : <><AlertCircle size={12} /> Draft Preview</>}
                </div>
                {sc && (
                  <div className="relative flex items-center justify-center">
                    <ScoreRing value={sc.overall_score || 0} size={88} stroke={7} color="#059669" />
                    <div className="absolute text-center">
                      <span className="text-xl font-bold text-white tabular-nums">{sc.overall_score}</span>
                      <span className="text-xs text-slate-400 block -mt-0.5">/ 100</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Organization Identity ─────────────────────────────────────── */}
          <div className="px-8 py-6 border-b border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">Organization Identity</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {[
                { label: "Organization", value: org?.name },
                { label: "Org Type", value: org?.type ? org.type.charAt(0).toUpperCase() + org.type.slice(1) : "—" },
                { label: "Sector", value: org?.sector || "—" },
                { label: "Country", value: org?.country || "—" }
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-slate-400 mb-0.5 uppercase tracking-wider">{label}</p>
                  <p className="text-sm font-semibold text-slate-900">{value || "—"}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Assessment Context ──────────────────────────────────────────── */}
          <div className="px-8 py-5 border-b border-slate-100 bg-slate-50">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {[
                { label: "Assessment", value: passport.assessment?.title },
                { label: "Reporting Year", value: passport.assessment?.reporting_year || "—" },
                { label: "Geography", value: passport.assessment?.geography?.toUpperCase() || "—" },
                { label: "Status", value: passport.assessment?.status?.replace(/_/g, " ") }
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-slate-400 mb-0.5 uppercase tracking-wider">{label}</p>
                  <p className="text-sm font-semibold text-slate-900 capitalize">{value || "—"}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Headline Emissions Metrics ──────────────────────────────────── */}
          {sc && (
            <div className="px-8 py-7 border-b border-slate-100">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-5">Headline Climate Metrics</p>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { label: "Total Emissions", value: `${sc.total_emissions_tco2e?.toFixed(2)}`, unit: "tCO₂e" },
                  { label: "Scope 1 Direct", value: `${sc.scope_1_tco2e?.toFixed(2) ?? (sc.scope_1_kgco2e / 1000).toFixed(2)}`, unit: "tCO₂e" },
                  { label: "Scope 2 Indirect", value: `${sc.scope_2_tco2e?.toFixed(2) ?? (sc.scope_2_kgco2e / 1000).toFixed(2)}`, unit: "tCO₂e" },
                ].map(({ label, value, unit }) => (
                  <MetricCard key={label} label={label} value={value} unit={unit} />
                ))}
              </div>

              {/* Category breakdown */}
              {sc.by_category && Object.keys(sc.by_category).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {Object.entries(sc.by_category).map(([cat, kg]) => (
                    <div key={cat} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg">
                      <span className="text-xs font-medium text-slate-500 capitalize">{cat}</span>
                      <span className="text-xs font-semibold tabular-nums text-slate-900">{(kg / 1000).toFixed(2)} t</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Five Pillars Scorecard ──────────────────────────────────────── */}
          {sc && (
            <div className="px-8 py-7 border-b border-slate-100">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-5">Five-Pillar Performance Scorecard</p>
              <div className="space-y-4">
                {pillars.map(({ id, label, desc, value, color }) => (
                  <div key={id}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{label}</p>
                        <p className="text-xs text-slate-400">{desc}</p>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${value}%`, backgroundColor: color }} />
                        </div>
                        <span className="text-sm font-bold tabular-nums w-12 text-right" style={{ color }}>{value}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Data Confidence Score ────────────────────────────────────────── */}
          {sc?.confidence_classification && (
            <div className="px-8 py-6 border-b border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Data Confidence Score</p>
                {confConf && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${confConf.class}`}>
                    <div className={`w-2 h-2 rounded-full ${confConf.dot}`} />
                    {sc.data_confidence}% — {confConf.label}
                  </span>
                )}
              </div>
              {sc.confidence_components && (
                <div className="space-y-3">
                  {[
                    { label: "Evidence Coverage", key: "evidence_coverage", weight: "35%" },
                    { label: "Data Completeness", key: "data_completeness", weight: "25%" },
                    { label: "Factor Reliability", key: "factor_reliability", weight: "20%" },
                    { label: "Source Quality", key: "source_quality", weight: "20%" },
                  ].map(({ label, key, weight }) => {
                    const val = sc.confidence_components[key] || 0;
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-600">{label} <span className="text-slate-400">({weight})</span></span>
                          <span className="text-xs font-semibold text-[#059669]">{val}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#059669] rounded-full" style={{ width: `${val}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Methodology & Factor Governance ─────────────────────────────── */}
          <div className="px-8 py-6 border-b border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">Methodology & Governance</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Shield size={16} className="text-[#059669] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-0.5">Accounting Standard</p>
                    <p className="text-sm font-bold text-slate-900">{meth?.name || "GHG Protocol Corporate Standard"}</p>
                    <p className="text-xs text-slate-500">Version {meth?.version || "2.0"} · {meth?.standard || "GHG Protocol / ISO 14064"}</p>
                  </div>
                </div>
              </div>
              {sc?.calculation_hash && (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Hash size={16} className="text-[#059669] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-0.5">Calculation Fingerprint</p>
                      <p className="text-xs font-mono text-slate-900 break-all">{sc.calculation_hash}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{sc.factor_count || 0} factor version(s) locked</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-3 leading-relaxed">
              All emissions calculations are traceable to approved methodology versions and factor libraries. Factor values are locked at calculation time to ensure reproducibility.
              {sc?.calculated_at && ` Calculated: ${new Date(sc.calculated_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.`}
            </p>
          </div>

          {/* ── Validity & Registry ──────────────────────────────────────────── */}
          <div className="px-8 py-6 border-b border-slate-100">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Calendar size={13} className="text-slate-400" />
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Issue Date</p>
                </div>
                <p className="text-sm font-semibold text-slate-900">
                  {passport.assessment?.updated_at
                    ? new Date(passport.assessment.updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                    : "Pending issue"}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Calendar size={13} className="text-slate-400" />
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Validity Period</p>
                </div>
                <p className="text-sm font-semibold text-slate-900">12 months from issue</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Award size={13} className="text-slate-400" />
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Passport ID</p>
                </div>
                <p className="text-xs font-mono font-bold text-slate-900">{passport.passport_id}</p>
              </div>
            </div>
          </div>

          {/* ── Registry & QR Code ──────────────────────────────────────────── */}
          {hasRegistry ? (
            <div className="px-8 py-6 bg-emerald-50 border-b border-emerald-200">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Globe size={15} className="text-[#059669]" />
                    <p className="text-xs font-semibold uppercase tracking-widest text-emerald-800">Published to Public Registry</p>
                  </div>
                  <p className="text-sm font-bold text-slate-900 mb-1">{registryId}</p>
                  <p className="text-xs text-slate-500">
                    Issued: {reg.issue_date ? new Date(reg.issue_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"} ·
                    Expires: {reg.expiry_date ? new Date(reg.expiry_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                  </p>
                  <a href={`/verify/${registryId}`} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-[#059669] font-medium hover:underline mt-2">
                    View verification page <ExternalLink size={11} />
                  </a>
                </div>
                <div className="flex-shrink-0 text-center">
                  {!qrError ? (
                    <img
                      src={`${BACKEND_URL}/api/registry/${registryId}/qr`}
                      alt="QR Code"
                      className="w-20 h-20 border border-emerald-200 rounded-lg"
                      onError={() => setQrError(true)}
                    />
                  ) : (
                    <div className="w-20 h-20 bg-white border border-emerald-200 rounded-lg flex items-center justify-center">
                      <Globe size={20} className="text-emerald-300" />
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-1">Scan to verify</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-8 py-5 border-b border-slate-100">
              <div className="flex items-center gap-3 text-slate-400">
                <Globe size={15} />
                <p className="text-sm">Registry publication pending — issue the assessment report to publish to the public registry.</p>
              </div>
            </div>
          )}

          {/* ── Footer ─────────────────────────────────────────────────────── */}
          <div className="px-8 py-4 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-[#0F172A] rounded px-2 py-1">
                <img src={LOGO_URL} alt="DeCarbonIQ" className="h-4 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
              </div>
              <span className="text-xs text-slate-400">DeCarbonIQ Climate Intelligence Platform</span>
            </div>
            <span className="text-xs font-mono text-slate-300">{passport.passport_id}</span>
          </div>
        </div>

        {/* Passport Version History */}
        {versions.length > 0 && (
          <SectionCard>

            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <History size={14} /> Passport Version History
            </h3>
            <div className="space-y-2">
              {versions.map(v => (
                <div key={v.id} className={`flex items-center justify-between p-3 rounded-lg border ${v.is_current ? "border-emerald-200 bg-emerald-50" : "border-slate-100 bg-slate-50"}`}>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 font-mono">{v.passport_id}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      v{v.version_number} · Issued {v.created_at ? new Date(v.created_at).toLocaleDateString("en-GB") : "—"}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${v.status === "active" ? "bg-emerald-50 text-emerald-700" : v.status === "superseded" ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-700"}`}>
                    {v.is_current ? "Current" : v.status}
                  </span>
                </div>
              ))}
            </div>
                    </SectionCard>
        )}

        {/* Growth Loop — Badge Embed */}
        {hasRegistry && (
          <BadgeEmbed registryId={registryId} passportId={passport.passport_id} />
        )}

        {/* Verification Events */}
        {verificationEvents.length > 0 && (
          <SectionCard>

            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Shield size={14} /> Verification Audit Trail
            </h3>
            <div className="space-y-2">
              {verificationEvents.map((ev, i) => (
                <div key={i} className="flex items-start gap-3 p-3 border border-slate-100 rounded-lg">
                  <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield size={12} className="text-[#059669]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 capitalize">{ev.action?.replace(/_/g, " ")}</p>
                    <p className="text-xs text-slate-400">{ev.verifier_email} · {ev.verifier_role} · {ev.created_at ? new Date(ev.created_at).toLocaleDateString("en-GB") : "—"}</p>
                    {ev.notes && <p className="text-xs text-slate-500 mt-0.5">{ev.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
                    </SectionCard>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-2">
          <button onClick={() => navigate(`/assessments/${id}`)} className="flex-1 h-10 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
            Back to Assessment
          </button>
          <button onClick={() => navigate("/reports")} className="flex-1 h-10 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] transition-colors">
            Manage Reports & Registry
          </button>
        </div>
      </div>
    </div>
  );
}
